// lib/phone.ts · v1.2 · v12.10.27 (#2155 a leading 1 typed into the box is the country code) · 2026-10-31 · VoiceSpectrum AI
// lib/phone.ts · v1.1 · v12.10.5.1 (#1135 the mask's own +1 is never a digit · #1136 Backspace through the mask · #1137 no area-code rule) · v1.0 · v12.10.5 (#1122, T2_UI_PRINCIPLES §38 + §39) · 2026-10-03 · VoiceSpectrum AI
//
// §38 PHONE NUMBERS: the field starts blank; the mask appears on the first digit; paste anything. US and Canada only
// for now: +1 (908) 688-8810 on screen, E.164 (+19086888810) on the wire. A '+' with any other country code is kept
// as typed and refused with "Only US and Canada numbers for now". 12.10.5 applies it to the three Handoff lines only;
// everywhere else is #1134 (parked until Tushar reviews this one).
// §39 "LAST DONE" STAMPS: "+1 (908) 688-8810 · 3 days ago" - today / yesterday / N days ago; the exact date and time
// on hover, e.g. "Sep 30, 2026 12:20 PM ET".

export const ONLY_US_CANADA = 'Only US and Canada numbers for now';

/** True when the text names another country (a '+' not followed by 1). */
export function isForeign(raw: string | null | undefined): boolean {
  return /^\s*\+\s*[02-9]/.test(raw ?? '');
}

const onlyDigits = (t: string) => t.replace(/\D/g, '');

/**
 * v1.1 (#1135 / #1137): the national digits of a stored or typed value - never the mask's own "+1".
 * "+19086888810" / "+1 (908) 688-8810" -> "9086888810"; "1-908-688-8810" -> "9086888810"; "413" -> "413".
 */
function nationalDigits(raw: string | null | undefined): string {
  const t = (raw ?? '').trim();
  if (!t || isForeign(t)) return '';
  if (/^\+\s*1/.test(t)) return onlyDigits(t).slice(1, 11);
  let d = onlyDigits(t);
  if (d.length === 11 && d[0] === '1') d = d.slice(1);
  return d.slice(0, 10);
}

/** +19086888810 for a complete US/Canada number (10 digits - no area-code rule, #1137), else null. */
export function toE164(raw: string | null | undefined): string | null {
  const d = nationalDigits(raw);
  return d.length === 10 ? `+1${d}` : null;
}

/** What the box shows: blank until the first digit, then the mask; +1 is part of the mask (#1137). Foreign text as typed. */
export function maskPhone(raw: string | null | undefined): string {
  const t = raw ?? '';
  if (isForeign(t)) return t;
  const d = nationalDigits(t);
  if (!d) return '';
  if (d.length <= 3) return `+1 (${d}`;
  if (d.length <= 6) return `+1 (${d.slice(0, 3)}) ${d.slice(3)}`;
  return `+1 (${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

/**
 * v1.1 (#1135 / #1136): the value kept in the draft for what is now in the box.
 * Typed into the mask: the leading "+1" is the mask, never a digit, so 4-1-3 reads 413. A paste is read whole.
 * Backspace / Delete over a mask character removes the last digit (the mask would only put the character back).
 * Returns "+1XXXXXXXXXX" when complete, the national digits while typing, "" when empty.
 */
export function phoneFromInput(typed: string, previous?: string | null): string {
  if (!typed.trim()) return '';
  if (isForeign(typed)) return typed;
  let d = /^\s*\+/.test(typed) && !/^\s*\+\s*1/.test(typed)
    ? onlyDigits(typed).slice(0, 10)                      // the user removed the 1 of "+1": every digit is the number
    : nationalDigits(typed);
  const prevShown = maskPhone(previous);
  const prevDigits = nationalDigits(previous);
  if (prevShown && typed.length < prevShown.length && d === prevDigits) d = d.slice(0, -1);   // deleted a mask character
  // v12.10.27 (#2155): a leading 1 typed key by key is the country code - no US / Canada area code starts with 1. Without
  // this, 1-9-7-3-… became +1 (197) 355-5014 (the 1 took the first area-code slot and the last digit fell off).
  if (!/^\s*\+/.test(typed) && d.startsWith('1')) d = d.slice(1);
  if (!d) return '';
  return d.length === 10 ? `+1${d}` : d;
}

/** The error under the box, or '' when the value is blank or a complete number. */
export function phoneError(raw: string | null | undefined): string {
  const t = (raw ?? '').trim();
  if (!t || toE164(t)) return '';
  if (isForeign(t)) return ONLY_US_CANADA;
  return 'Type all 10 digits';
}

const ET = 'America/New_York';

/** §39: "today" / "yesterday" / "N days ago", counted in Eastern time. */
export function agoWords(iso: string, now: Date = new Date()): string {
  const day = (d: Date) => new Intl.DateTimeFormat('en-CA', { timeZone: ET, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
  const a = Date.parse(day(new Date(iso)));
  const b = Date.parse(day(now));
  const n = Math.round((b - a) / 86_400_000);
  if (n <= 0) return 'today';
  if (n === 1) return 'yesterday';
  return `${n} days ago`;
}

/** §39 hover text: "Sep 30, 2026 12:20 PM ET". */
export function exactEt(iso: string): string {
  const d = new Date(iso);
  const date = new Intl.DateTimeFormat('en-US', { timeZone: ET, month: 'short', day: 'numeric', year: 'numeric' }).format(d);
  const time = new Intl.DateTimeFormat('en-US', { timeZone: ET, hour: 'numeric', minute: '2-digit' }).format(d);
  return `${date} ${time} ET`;
}
